import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridPreProcessEditCellProps,
  GridRowEditStopReasons,
  GridRowModel,
  GridRowModesModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import { Box, Button, CircularProgress } from '@mui/material';
import Collapsible from 'components/collapsible';
import DataGrid from 'components/data-grid';
import { Option } from 'components/data-grid/data-grid-autocomplete';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { MinusCircle, PlusCircle, TimerReset } from 'lucide-react';
import Mui from 'materials';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import _ from 'underscore';
import { randomUUID } from 'utils/random';
import { isValidInteger } from 'utils/validate-numbers';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { getFeeds } from 'views/bluviu-nutrition/api/get-feeds';
import DataGridFeedAutocomplete from 'views/bluviu-nutrition/components/data-grid-feed-autocomplete';
import { renderFeedOption } from 'views/bluviu-nutrition/components/render-feed-option';
import {
  getR2CalibrationFeedProgram,
  putR2CalibrationFeedProgram,
} from 'views/bluviu-nutrition/features/calibrations/api/r2-calibration';
import { CalibrationsParams } from 'views/bluviu-nutrition/features/calibrations/types/calibrations-params.type';
import { UpdateCalibrationFeedProgramRequest } from 'views/bluviu-nutrition/features/calibrations/types/update-calibration-feed-program-request.type';
import { FeedOption } from 'views/bluviu-nutrition/types/feed-option.type';
import { FeedSource } from 'views/bluviu-nutrition/types/feed-source.enum';

const headerCellStyle: React.CSSProperties = {
  whiteSpace: 'normal',
  lineHeight: 1,
};

type UpdateFeedProgramFeedForm = {
  id: string; // unique identifier for the data grid
  feed: FeedOption;
  fishSizeStart: number;
};

type Props = {};

const PAGE_NAME_ID = `bluViuNutrition.calibrations.editCalibration.feedProgram`;

const R2CalibrationFeedProgram: React.FC<Props> = ({}) => {
  const intl = useIntl();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();
  const params = useParams<CalibrationsParams>();
  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();
  const {
    account: { company },
  } = useAccountState();

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const defaultRow: UpdateFeedProgramFeedForm = {
    feed: { id: '', label: '', feedSource: FeedSource.MANUAL, isActive: true },
    id: randomUUID(),
    fishSizeStart: 0,
  };

  const { data: feeds } = useGet(['getFeeds', company!.companyId], ({ queryKey }) => getFeeds(String(queryKey[1])), {
    enabled: !!company?.companyId,
  });

  const { data: feedPrograms, isLoading: isFeedProgramsLoading } = useGet(
    ['getR2CalibrationFeedPrograms', params.globalSiteId, params.calibrationId],
    ({ queryKey }) => getR2CalibrationFeedProgram(String(queryKey[1]), String(queryKey[2]))
  );

  const defaultRows: UpdateFeedProgramFeedForm[] = useMemo(() => {
    if (!feedPrograms?.length) return [defaultRow];
    else
      return _.chain(feedPrograms)
        .sortBy('fishSizeStart')
        .map((row) => {
          return {
            feed: {
              id: row.feedId,
              label: row.feedName,
              feedSource: row.feedSource,
              isActive: row.isActive,
            },
            id: randomUUID(),
            fishSizeStart: row.fishSizeStart,
          };
        })
        .value();
  }, [feedPrograms, feeds]);

  const [rows, setRows] = useState<UpdateFeedProgramFeedForm[]>(defaultRows);

  useEffect(() => {
    setRows(defaultRows); // memo isn't triggering state update for some reason, this forces a state update when default rows is updated
  }, [defaultRows]);

  const updateCalibrationFeedProgram = usePost<AxiosResponse, UpdateCalibrationFeedProgramRequest[]>(
    (request) => putR2CalibrationFeedProgram(params.globalSiteId, params.calibrationId, request),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Feed program saved!' },
      });
      queryClient.invalidateQueries({
        queryKey: ['getR2CalibrationFeedPrograms', params.globalSiteId, params.calibrationId]
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const handleFeedProgramSubmit = () => {
    const data: UpdateCalibrationFeedProgramRequest[] = rows.map((row) => {
      return {
        fishSizeStart: row.fishSizeStart,
        feedId: row.feed.id,
        feedSource: row.feed.feedSource,
      } as UpdateCalibrationFeedProgramRequest;
    });

    updateCalibrationFeedProgram.mutate(data);
  };

  const feedOptions: FeedOption[] = useMemo(() => {
    if (!feeds?.length) return [];

    return feeds.map((feed) => ({ id: feed.id, label: feed.name, feedSource: feed.feedSource, isActive: feed.active }));
  }, [feeds]);

  //#region Data Grid

  // #region HANDLERS

  const processRowUpdate = (newRow: GridRowModel<UpdateFeedProgramFeedForm>) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowInsert = (rowId: string) => {
    setRows((prevRow) => {
      const idx = prevRow.findIndex((row) => row.id === rowId);
      const newRows = [...prevRow];
      newRows.splice(idx + 1, 0, defaultRow);
      return newRows;
    });
    handleEditing();
  };

  const handleRowDelete = (id: string) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  // #endregion HANDLERS

  // #region COLUMNS

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.columns.feed` }),
        field: 'feed',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        editable: true,
        type: 'singleSelect',
        valueFormatter: (value: Option) => value.label,
        valueOptions: feedOptions,
        renderCell: renderFeedOption,
        renderEditCell: (params) => <DataGridFeedAutocomplete params={params} options={feedOptions} />,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !params.props.value;
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-feed`]: hasError });
          return { ...params.props, error: hasError };
        },
        sortable: false,
      } as GridColDef<any, FeedOption, string>,

      {
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.columns.quantity` }),
        field: 'fishSizeStart',
        flex: 1,
        type: 'number',
        width: '30%',
        align: 'right',
        editable: true,
        headerAlign: 'left',
        renderCell: (props) => {
          return <FormattedNumber value={props.row.fishSizeStart} />;
        },
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidInteger(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-sizeStart`]: hasError });
          return { ...params.props, error: hasError };
        },
        renderHeader: (params) => {
          return <span style={headerCellStyle}>{params.colDef.headerName}</span>;
        },
      },
      {
        headerName: '',
        field: 'actions',
        flex: 0.5,
        align: 'center',
        editable: false,
        headerAlign: 'center',
        type: 'actions',
        cellClassName: 'actions',
        getActions: (params) => {
          const id = params.row.id;
          return [
            <GridActionsCellItem
              icon={<MinusCircle />}
              disabled={rows.length === 1}
              label={intl.formatMessage({ id: `general.removeLine` })}
              onClick={() => handleRowDelete(id)}
            />,
            <GridActionsCellItem
              label={intl.formatMessage({ id: `general.addLine` })}
              icon={<PlusCircle />}
              onClick={() => handleRowInsert(params.row.id)}
            />,
          ];
        },
      },
    ] as GridColDef[];
  }, [rows, feedOptions]);

  // #endregion COLUMNS

  // #region HANDLERS

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const disableSubmit = useMemo(() => {
    return rows.some((row) => !row.feed.id);
  }, [rows]);

  const isEqual = useMemo(() => {
    if (defaultRows.length === rows.length) {
      const allRowsExist = rows.every((row) =>
        defaultRows.some((defaultRow) => row.feed.id === defaultRow.feed.id && row.fishSizeStart === defaultRow.fishSizeStart)
      );
      if (allRowsExist) return true;
      return false;
    }
    return false;
  }, [defaultRows, rows]);

  //#endregion HANDLERS

  //#endregion Data Grid
  if (isFeedProgramsLoading) return <LoadingSpinner />;

  return (
    <Collapsible label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })} open>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Box width={'100%'}>
          <DataGrid
            hideFooter
            autoHeight
            rows={rows}
            columns={columns}
            apiRef={apiRef}
            disableColumnMenu
            getRowId={(row) => row.id}
            rowModesModel={rowModesModel}
            onRowEditStop={handleRowEditStop}
            onCellEditStop={({ field }) => {
              if (field === 'feed') return;
              handleEditing();
            }}
            processRowUpdate={processRowUpdate}
            onRowClick={({ id }) => {
              if (!isEditing) {
                const { field } = apiRef.current?.state.tabIndex.cell ?? {};
                if (field === 'feed') return;
                apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
              }
            }}
            onRowModesModelChange={handleRowModesModelChange}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
          />
        </Box>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Box display={'flex'} alignItems={'center'}>
            <Mui.Tooltip title={'Reset feed program'}>
              <Mui.IconButton disabled={isEqual} onClick={() => setRows(defaultRows)}>
                <TimerReset size={22} />
              </Mui.IconButton>
            </Mui.Tooltip>
          </Box>
          <Button
            type="submit"
            size="medium"
            disabled={updateCalibrationFeedProgram.isPending || isEditing || hasError || disableSubmit || isEqual}
            startIcon={updateCalibrationFeedProgram.isPending ? <CircularProgress color="inherit" size={18} /> : null}
            onClick={() => handleFeedProgramSubmit()}>
            <FormattedMessage id="general.save" />
          </Button>
        </Box>
      </Box>
    </Collapsible>
  );
};

export default React.memo(R2CalibrationFeedProgram);
